import { ContextMenu as BaseContextMenu } from '@base-ui/react/context-menu';
import { mergeProps } from '@base-ui/react/merge-props';
import type { ReactElement } from 'react';
import { SheetGrabber, resolveDismiss } from '../layers/base';
import { useIsCompact } from '../layers/compact';
import type { LayerCloseReason } from '../layers/types';
import { LayerParent, useLayer } from '../layers/useLayer';
import { type MenuItem, useMenuState } from './Menu';
import { MenuItems } from './MenuPopup';
import { mergeRefs } from './refs';

export type ContextMenuProps = {
  /** The area that opens the menu on right click, long press, Shift+F10 or the Menu key. */
  children: ReactElement;
  items: MenuItem[];
  label?: string;
  onOpenChange?: (open: boolean, reason?: LayerCloseReason) => void;
};

/** Context menu on the layer stack: right click, long press (touch), Shift+F10 and the ContextMenu key. */
export function ContextMenu({ children, items, label, onOpenChange }: ContextMenuProps) {
  const [open, setOpen] = useMenuState(undefined, onOpenChange);
  const compact = useIsCompact();
  const layer = useLayer({
    kind: compact ? 'sheet' : 'menu',
    open,
    onClose: (reason) => setOpen(false, reason),
    escape: 'self',
    label,
  });
  return (
    <BaseContextMenu.Root
      open={open}
      onOpenChange={(next, details) => {
        if (next) return setOpen(true);
        const reason = resolveDismiss(layer, details);
        if (reason) setOpen(false, reason);
      }}
    >
      <BaseContextMenu.Trigger
        render={(props) => {
          const own = children.props as Record<string, unknown>;
          // Keyboard path: Shift+F10 or the ContextMenu key open the menu at the element.
          const keyboard = {
            onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
              if (e.defaultPrevented || !((e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu')) return;
              e.preventDefault();
              const rect = e.currentTarget.getBoundingClientRect();
              e.currentTarget.dispatchEvent(
                new MouseEvent('contextmenu', {
                  bubbles: true,
                  cancelable: true,
                  clientX: rect.left + Math.min(24, rect.width / 2),
                  clientY: rect.top + Math.min(rect.height, 24),
                  button: 2,
                }),
              );
            },
          };
          const Type = children.type as React.ElementType;
          return <Type {...mergeProps(keyboard, props, own)} ref={mergeRefs(props.ref, own.ref)} />;
        }}
      />
      <BaseContextMenu.Portal container={layer.container} className="dr-layer" {...layer.portalProps}>
        {compact ? <BaseContextMenu.Backdrop className="scrim sheet-scrim" /> : null}
        <BaseContextMenu.Positioner
          className="dr-positioner"
          collisionPadding={8}
          data-sheet={compact || undefined}
        >
          <BaseContextMenu.Popup
            className="popover menu"
            aria-label={label}
            data-sheet={compact || undefined}
            // An item that moved focus on (Rename opens an input) keeps it; otherwise focus goes back.
            finalFocus={layer.finalFocus}
          >
            {compact ? <SheetGrabber onDismiss={() => void layer.requestClose('swipe')} /> : null}
            <LayerParent id={layer.id}>
              <MenuItems items={items} parts={BaseContextMenu} layer={layer} compact={compact} />
            </LayerParent>
          </BaseContextMenu.Popup>
        </BaseContextMenu.Positioner>
      </BaseContextMenu.Portal>
    </BaseContextMenu.Root>
  );
}
